/**
 * What a long form says about where the agent's typing is (hooks/useFormAutosave):
 * a one-line status, and the banner a reloaded page shows when it brought back a
 * device copy. Plain elements on the design tokens, so the phone and the desktop
 * shell can both use them. Mirrors cid_admin_portal/src/components/common/AutosaveStatus.jsx.
 */
import dayjs from 'dayjs';

const clock = (iso) => dayjs(iso).format('h:mm A');

/**
 * `idleText` — shown when nothing is pending.
 * `savedLabel` — what a server save is called ("Saved to draft OTP-202600083").
 * `waitingText` — why autosave has not started (canServerSave() said no).
 */
export function AutosaveStatus({ autosave, idleText = 'Save any time', savedLabel = 'Saved to draft', waitingText = 'Fill in the required fields to start saving automatically', style }) {
  const { dirty, deviceSavedAt, server, toServer } = autosave;
  let body = idleText;
  let color = 'var(--muted)';
  if (toServer) {
    if (server.state === 'saving') body = 'Saving…';
    else if (server.state === 'error') { body = `Not saved — ${server.error}${deviceSavedAt ? ' (kept on this device)' : ''}`; color = 'var(--bad)'; }
    else if (server.state === 'waiting') body = waitingText;
    else if (server.state === 'saved' && !dirty) { body = `✓ ${savedLabel} · ${clock(server.at)}`; color = 'var(--ok)'; }
    else if (!dirty) body = 'Changes save automatically';
    else body = 'Unsaved changes…';
  } else if (dirty && deviceSavedAt) {
    body = `Kept on this device · ${clock(deviceSavedAt)} — not saved yet`;
  }
  return <span data-demo="save-status" style={{ fontSize: 11.5, color, lineHeight: 1.4, ...style }}>{body}</span>;
}

/** Shown after a reload brought back a device copy. `onServer` — the record already exists. */
export function RestoredDraftBanner({ autosave, onServer = false, notYet = 'It is not saved yet — save it to keep it.', style }) {
  if (!autosave.restored) return null;
  return (
    <div
      data-demo="restored-banner"
      style={{
        background: 'color-mix(in srgb, var(--primary) 8%, transparent)',
        border: '1px solid color-mix(in srgb, var(--primary) 30%, transparent)',
        borderRadius: 12, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start', ...style,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>
          Restored what you were typing on this device at {clock(autosave.restored.savedAt)}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
          {onServer
            ? 'The page was reloaded or closed before these changes were saved.'
            : `The page was reloaded or closed before this was saved. ${notYet}`}
        </div>
      </div>
      <button
        type="button"
        onClick={autosave.discardRestored}
        style={{ flex: 'none', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}
      >
        Discard it
      </button>
    </div>
  );
}
